// Example usage:
/*
const result = await confirm({
  title: 'Reverse Transaction',
  message: 'This transaction will be adjusted to reflect the reduced quantity.',
  confirmText: 'Reverse',
  cancelText: 'Cancel',
  confirmType: 'is-primary'
});

if (result) {
  console.log('User confirmed');
} else {
  console.log('User cancelled');
}

const input = await prompt({
  title: 'Enter Name',
  message: 'Please enter your name',
  placeholder: 'John Doe',
  defaultValue: '',
  confirmText: 'Submit',
  cancelText: 'Cancel',
  validator: (value) => {
    if (!value) return 'Name is required';
    if (value.length < 3) return 'Name must be at least 3 characters';
    return undefined; // Valid
  }
});

if (input !== null) {
  console.log('User entered:', input);
} else {
  console.log('User cancelled');
}

await alert({
  title: 'Success',
  message: 'Your changes have been saved successfully.',
  confirmText: 'OK',
  confirmType: 'is-success'
});
*/

interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmType?: 'is-primary' | 'is-danger' | 'is-success' | 'is-warning';
}

interface PromptOptions {
    title?: string;
    message: string;
    placeholder?: string;
    defaultValue?: string;
    confirmText?: string;
    cancelText?: string;
    confirmType?: 'is-primary' | 'is-danger' | 'is-success' | 'is-warning';
    validator?: (value: string) => string | undefined;
}

interface AlertOptions {
    title?: string;
    message: string;
    confirmText?: string;
    confirmType?: 'is-primary' | 'is-danger' | 'is-success' | 'is-warning';
}

// Shared modal styling constants
const MODAL_STYLES = {
    container: `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 5500;
      animation: backdropFadeIn 0.2s ease;
    `,
    backdrop: `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 5499;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    `,
    wrapper: `
      position: relative;
      z-index: 5500;
      display: flex;
      flex-direction: column;
      width: min(calc(100vw - 30px), 520px);
      max-width: min(calc(100vw - 30px), 520px);
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      animation: modalScaleIn 0.33s ease;
    `,
    header: `
      background: #eaf0f9;
      padding: 12px 12px 12px 24px;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    `,
    title: `
      margin: 0;
      font-weight: 400;
      color: #1a1a1a;
      font-size: 18px;
      line-height: 1;
      position: relative;
      top: 1px;
    `,
    closeButton: `
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      margin-left: auto;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
    `,
    content: `
      background: white;
      padding: 24px;
      max-height: 80vh;
      overflow-y: auto;
    `,
    message: `
      margin: 0;
      color: #374151;
      font-size: 14px;
      line-height: 1.5;
      white-space: pre-line;
    `,
    footer: `
      background: white;
      padding: 16px 24px;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
      border-top: 1px solid rgba(0, 0, 0, 0.06);
    `,
    baseButton: `
      position: relative;
      padding: 6px 14px;
      font-size: 14px;
      font-weight: 500;
      border: 1px solid;
      text-align: center;
      border-radius: 6px;
      transition: all 0.2s ease-in-out;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      cursor: pointer;
    `,
    cancelButton: `
      background: white;
      color: #374151;
      border-color: #d1d5db;
    `,
    input: `
      font-size: 14px;
      border: 1px solid #d1d5db;
      background: white;
      width: 100%;
      padding: 6px 14px;
      border-radius: 6px;
      color: #111827;
      transition: all 0.15s;
      outline: none;
    `,
    errorMessage: `
      margin-top: 8px;
      font-size: 13px;
      color: #dc2626;
      min-height: 18px;
    `,
} as const;

const MODAL_ANIMATIONS = `
  @keyframes backdropFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes backdropFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes modalScaleIn {
    from {
      opacity: 0;
      transform: scale(0.7);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  @keyframes modalScaleOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.7);
    }
  }
`;

// Button color schemes
const getButtonColorScheme = (
    type: string
): {
    background: string;
    color: string;
    borderColor: string;
    hoverBg: string;
    hoverBorder: string;
    activeBg: string;
} => {
    if (type === 'is-danger') {
        return {
            background: 'white',
            color: '#dc2626',
            borderColor: '#fca5a5',
            hoverBg: '#fef2f2',
            hoverBorder: '#f87171',
            activeBg: '#fee2e2',
        };
    }
    if (type === 'is-success') {
        return {
            background: '#10b981',
            color: 'white',
            borderColor: '#10b981',
            hoverBg: 'rgba(16, 185, 129, 0.9)',
            hoverBorder: 'rgba(16, 185, 129, 0.9)',
            activeBg: 'rgba(16, 185, 129, 0.8)',
        };
    }
    if (type === 'is-warning') {
        return {
            background: '#f59e0b',
            color: 'white',
            borderColor: '#f59e0b',
            hoverBg: 'rgba(245, 158, 11, 0.9)',
            hoverBorder: 'rgba(245, 158, 11, 0.9)',
            activeBg: 'rgba(245, 158, 11, 0.8)',
        };
    }
    return {
        background: '#2563eb',
        color: 'white',
        borderColor: '#2563eb',
        hoverBg: 'rgba(37, 99, 235, 0.9)',
        hoverBorder: 'rgba(37, 99, 235, 0.9)',
        activeBg: 'rgba(37, 99, 235, 0.8)',
    };
};

/**
 * Creates a styled confirmation modal matching the app's design system
 * Returns a promise that resolves to true/false based on user's choice
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
    const {
        title = 'Confirm Action',
        message,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmType = 'is-primary',
    } = options;

    return new Promise((resolve) => {
        // Create container
        const container = document.createElement('div');
        container.style.cssText = MODAL_STYLES.container;

        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.style.cssText = MODAL_STYLES.backdrop;

        // Create modal wrapper
        const wrapper = document.createElement('div');
        wrapper.style.cssText = MODAL_STYLES.wrapper;

        // Prevent clicks on modal from closing it
        wrapper.onclick = (e) => e.stopPropagation();

        // Create header
        const header = document.createElement('div');
        header.style.cssText = MODAL_STYLES.header;

        const titleEl = document.createElement('h4');
        titleEl.textContent = title;
        titleEl.style.cssText = MODAL_STYLES.title;

        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '<span style="font-size: 24px;">×</span>';
        closeBtn.style.cssText = MODAL_STYLES.closeButton;
        closeBtn.onmouseover = () => {
            closeBtn.style.background = 'rgba(0, 0, 0, 0.04)';
            closeBtn.style.color = '#1a1a1a';
        };
        closeBtn.onmouseout = () => {
            closeBtn.style.background = 'transparent';
            closeBtn.style.color = '#6b7280';
        };

        header.appendChild(titleEl);
        header.appendChild(closeBtn);

        // Create content
        const content = document.createElement('div');
        content.style.cssText = MODAL_STYLES.content;

        const messageEl = document.createElement('p');
        messageEl.textContent = message;
        messageEl.style.cssText = MODAL_STYLES.message;

        content.appendChild(messageEl);

        // Create footer
        const footer = document.createElement('div');
        footer.style.cssText = MODAL_STYLES.footer;

        // Cancel button (secondary style)
        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = cancelText;
        cancelBtn.style.cssText = MODAL_STYLES.baseButton + MODAL_STYLES.cancelButton;
        cancelBtn.onmouseover = () => {
            cancelBtn.style.background = '#f9fafb';
            cancelBtn.style.borderColor = '#9ca3af';
            cancelBtn.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
        };
        cancelBtn.onmouseout = () => {
            cancelBtn.style.background = 'white';
            cancelBtn.style.borderColor = '#d1d5db';
            cancelBtn.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
        };
        cancelBtn.onmousedown = () => {
            cancelBtn.style.transform = 'scale(0.98)';
            cancelBtn.style.boxShadow = 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)';
        };
        cancelBtn.onmouseup = () => {
            cancelBtn.style.transform = 'scale(1)';
        };

        // Confirm button styles based on type
        const confirmBtn = document.createElement('button');
        confirmBtn.textContent = confirmText;

        const styles = getButtonColorScheme(confirmType);

        confirmBtn.style.cssText = MODAL_STYLES.baseButton;
        confirmBtn.style.background = styles.background;
        confirmBtn.style.color = styles.color;
        confirmBtn.style.borderColor = styles.borderColor;
        confirmBtn.dataset.hoverBg = styles.hoverBg;
        confirmBtn.dataset.hoverBorder = styles.hoverBorder;
        confirmBtn.dataset.activeBg = styles.activeBg;
        confirmBtn.onmouseover = () => {
            confirmBtn.style.background = confirmBtn.dataset.hoverBg!;
            confirmBtn.style.borderColor = confirmBtn.dataset.hoverBorder!;
            confirmBtn.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
        };
        confirmBtn.onmouseout = () => {
            confirmBtn.style.background = styles.background;
            confirmBtn.style.borderColor = styles.borderColor;
            confirmBtn.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
        };
        confirmBtn.onmousedown = () => {
            confirmBtn.style.transform = 'scale(0.98)';
            confirmBtn.style.background = confirmBtn.dataset.activeBg!;
            confirmBtn.style.boxShadow = 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)';
        };
        confirmBtn.onmouseup = () => {
            confirmBtn.style.transform = 'scale(1)';
        };

        footer.appendChild(cancelBtn);
        footer.appendChild(confirmBtn);

        // Assemble modal
        wrapper.appendChild(header);
        wrapper.appendChild(content);
        wrapper.appendChild(footer);

        container.appendChild(backdrop);
        container.appendChild(wrapper);

        // Add animations
        const styleId = 'confirm-modal-animations';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = MODAL_ANIMATIONS;
            document.head.appendChild(style);
        }

        // Cleanup function with animation
        const cleanup = () => {
            // Trigger exit animations
            container.style.animation = 'backdropFadeOut 0.2s ease forwards';
            wrapper.style.animation = 'modalScaleOut 0.33s ease forwards';

            // Wait for animation to complete before removing
            setTimeout(() => {
                if (document.body.contains(container)) {
                    document.body.removeChild(container);
                }
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            }, 330);
        };

        // Event handlers
        const handleConfirm = () => {
            cleanup();
            resolve(true);
        };

        const handleCancel = () => {
            cleanup();
            resolve(false);
        };

        confirmBtn.onclick = handleConfirm;
        cancelBtn.onclick = handleCancel;
        closeBtn.onclick = handleCancel;
        backdrop.onclick = handleCancel;

        // Handle ESC key
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCancel();
                document.removeEventListener('keydown', handleEsc);
            }
        };
        document.addEventListener('keydown', handleEsc);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '15px';

        // Add to DOM
        document.body.appendChild(container);
    });
}

/**
 * Creates a styled input modal matching the app's design system
 * Returns a promise that resolves to the input value or null if cancelled
 */
export function prompt(options: PromptOptions): Promise<string | null> {
    const {
        title = 'Input Required',
        message,
        placeholder = '',
        defaultValue = '',
        confirmText = 'Submit',
        cancelText = 'Cancel',
        confirmType = 'is-primary',
        validator,
    } = options;

    return new Promise((resolve) => {
        let inputValue = defaultValue;
        let errorMessage = '';

        // Create container
        const container = document.createElement('div');
        container.style.cssText = MODAL_STYLES.container;

        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.style.cssText = MODAL_STYLES.backdrop;

        // Create modal wrapper
        const wrapper = document.createElement('div');
        wrapper.style.cssText = MODAL_STYLES.wrapper;

        // Prevent clicks on modal from closing it
        wrapper.onclick = (e) => e.stopPropagation();

        // Create header
        const header = document.createElement('div');
        header.style.cssText = MODAL_STYLES.header;

        const titleEl = document.createElement('h4');
        titleEl.textContent = title;
        titleEl.style.cssText = MODAL_STYLES.title;

        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '<span style="font-size: 24px;">×</span>';
        closeBtn.style.cssText = MODAL_STYLES.closeButton;
        closeBtn.onmouseover = () => {
            closeBtn.style.background = 'rgba(0, 0, 0, 0.04)';
            closeBtn.style.color = '#1a1a1a';
        };
        closeBtn.onmouseout = () => {
            closeBtn.style.background = 'transparent';
            closeBtn.style.color = '#6b7280';
        };

        header.appendChild(titleEl);
        header.appendChild(closeBtn);

        // Create content
        const content = document.createElement('div');
        content.style.cssText = MODAL_STYLES.content;

        const messageEl = document.createElement('p');
        messageEl.textContent = message;
        messageEl.style.cssText = `
      margin: 0 0 16px 0;
      color: #374151;
      font-size: 14px;
      line-height: 1.5;
    `;

        // Create input field matching Textbox.vue styling
        const inputEl = document.createElement('input');
        inputEl.type = 'text';
        inputEl.value = defaultValue;
        inputEl.placeholder = placeholder;
        inputEl.style.cssText = MODAL_STYLES.input;

        // Create error message element
        const errorEl = document.createElement('div');
        errorEl.style.cssText = MODAL_STYLES.errorMessage;

        // Update input styling on focus/hover
        inputEl.onfocus = () => {
            if (!errorMessage) {
                inputEl.style.borderColor = '#6b7280';
            }
        };
        inputEl.onblur = () => {
            if (!errorMessage) {
                inputEl.style.borderColor = '#d1d5db';
            }
        };
        inputEl.onmouseover = () => {
            if (!errorMessage) {
                inputEl.style.borderColor = '#9ca3af';
            }
        };
        inputEl.onmouseout = () => {
            if (!errorMessage && inputEl !== document.activeElement) {
                inputEl.style.borderColor = '#d1d5db';
            }
        };

        // Validate and update error state
        const validateInput = () => {
            errorMessage = validator ? validator(inputValue) || '' : '';
            errorEl.textContent = errorMessage;

            if (errorMessage) {
                inputEl.style.borderColor = '#fca5a5';
                inputEl.style.setProperty('border-color', '#fca5a5', 'important');
                confirmBtn.disabled = true;
                confirmBtn.style.opacity = '0.5';
                confirmBtn.style.cursor = 'not-allowed';
            } else {
                inputEl.style.borderColor = '#d1d5db';
                confirmBtn.disabled = false;
                confirmBtn.style.opacity = '1';
                confirmBtn.style.cursor = 'pointer';
            }
        };

        // Handle input changes
        inputEl.oninput = () => {
            inputValue = inputEl.value;
            validateInput();
        };

        content.appendChild(messageEl);
        content.appendChild(inputEl);
        content.appendChild(errorEl);

        // Create footer
        const footer = document.createElement('div');
        footer.style.cssText = MODAL_STYLES.footer;

        // Cancel button (secondary style)
        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = cancelText;
        cancelBtn.style.cssText = MODAL_STYLES.baseButton + MODAL_STYLES.cancelButton;
        cancelBtn.onmouseover = () => {
            cancelBtn.style.background = '#f9fafb';
            cancelBtn.style.borderColor = '#9ca3af';
            cancelBtn.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
        };
        cancelBtn.onmouseout = () => {
            cancelBtn.style.background = 'white';
            cancelBtn.style.borderColor = '#d1d5db';
            cancelBtn.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
        };
        cancelBtn.onmousedown = () => {
            cancelBtn.style.transform = 'scale(0.98)';
            cancelBtn.style.boxShadow = 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)';
        };
        cancelBtn.onmouseup = () => {
            cancelBtn.style.transform = 'scale(1)';
        };

        // Confirm button styles based on type
        const confirmBtn = document.createElement('button');
        confirmBtn.textContent = confirmText;

        const styles = getButtonColorScheme(confirmType);

        confirmBtn.style.cssText = MODAL_STYLES.baseButton;
        confirmBtn.style.background = styles.background;
        confirmBtn.style.color = styles.color;
        confirmBtn.style.borderColor = styles.borderColor;
        confirmBtn.dataset.hoverBg = styles.hoverBg;
        confirmBtn.dataset.hoverBorder = styles.hoverBorder;
        confirmBtn.dataset.activeBg = styles.activeBg;

        confirmBtn.onmouseover = () => {
            if (!confirmBtn.disabled) {
                confirmBtn.style.background = confirmBtn.dataset.hoverBg!;
                confirmBtn.style.borderColor = confirmBtn.dataset.hoverBorder!;
                confirmBtn.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
            }
        };
        confirmBtn.onmouseout = () => {
            if (!confirmBtn.disabled) {
                confirmBtn.style.background = styles.background;
                confirmBtn.style.borderColor = styles.borderColor;
                confirmBtn.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
            }
        };
        confirmBtn.onmousedown = () => {
            if (!confirmBtn.disabled) {
                confirmBtn.style.transform = 'scale(0.98)';
                confirmBtn.style.background = confirmBtn.dataset.activeBg!;
                confirmBtn.style.boxShadow = 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)';
            }
        };
        confirmBtn.onmouseup = () => {
            if (!confirmBtn.disabled) {
                confirmBtn.style.transform = 'scale(1)';
            }
        };

        footer.appendChild(cancelBtn);
        footer.appendChild(confirmBtn);

        // Assemble modal
        wrapper.appendChild(header);
        wrapper.appendChild(content);
        wrapper.appendChild(footer);

        container.appendChild(backdrop);
        container.appendChild(wrapper);

        // Add animations (reuse from confirm modal)
        const styleId = 'confirm-modal-animations';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = MODAL_ANIMATIONS;
            document.head.appendChild(style);
        }

        // Cleanup function with animation
        const cleanup = () => {
            // Trigger exit animations
            container.style.animation = 'backdropFadeOut 0.2s ease forwards';
            wrapper.style.animation = 'modalScaleOut 0.33s ease forwards';

            // Wait for animation to complete before removing
            setTimeout(() => {
                if (document.body.contains(container)) {
                    document.body.removeChild(container);
                }
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            }, 330);
        };

        // Event handlers
        const handleConfirm = () => {
            // Run validation one last time
            validateInput();
            if (errorMessage) {
                return; // Don't submit if there's an error
            }
            cleanup();
            resolve(inputValue);
        };

        const handleCancel = () => {
            cleanup();
            resolve(null);
        };

        confirmBtn.onclick = handleConfirm;
        cancelBtn.onclick = handleCancel;
        closeBtn.onclick = handleCancel;
        backdrop.onclick = handleCancel;

        // Handle ESC key
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCancel();
                document.removeEventListener('keydown', handleEsc);
            }
        };
        document.addEventListener('keydown', handleEsc);

        // Handle Enter key in input
        inputEl.onkeydown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
            }
        };

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '15px';

        // Add to DOM and focus input
        document.body.appendChild(container);

        // Focus input after a short delay to ensure modal is rendered
        setTimeout(() => {
            inputEl.focus();
            inputEl.select();
        }, 100);

        // Run initial validation if validator is provided and defaultValue is set
        if (validator && defaultValue) {
            validateInput();
        }
    });
}

/**
 * Creates a styled alert modal matching the app's design system
 * Returns a promise that resolves when the user dismisses the alert
 */
export function alert(options: AlertOptions): Promise<void> {
    const { title = 'Alert', message, confirmText = 'OK', confirmType = 'is-primary' } = options;

    return new Promise((resolve) => {
        // Create container
        const container = document.createElement('div');
        container.style.cssText = MODAL_STYLES.container;

        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.style.cssText = MODAL_STYLES.backdrop;

        // Create modal wrapper
        const wrapper = document.createElement('div');
        wrapper.style.cssText = MODAL_STYLES.wrapper;

        // Prevent clicks on modal from closing it
        wrapper.onclick = (e) => e.stopPropagation();

        // Create header
        const header = document.createElement('div');
        header.style.cssText = MODAL_STYLES.header;

        const titleEl = document.createElement('h4');
        titleEl.textContent = title;
        titleEl.style.cssText = MODAL_STYLES.title;

        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '<span style="font-size: 24px;">×</span>';
        closeBtn.style.cssText = MODAL_STYLES.closeButton;
        closeBtn.onmouseover = () => {
            closeBtn.style.background = 'rgba(0, 0, 0, 0.04)';
            closeBtn.style.color = '#1a1a1a';
        };
        closeBtn.onmouseout = () => {
            closeBtn.style.background = 'transparent';
            closeBtn.style.color = '#6b7280';
        };

        header.appendChild(titleEl);
        header.appendChild(closeBtn);

        // Create content
        const content = document.createElement('div');
        content.style.cssText = MODAL_STYLES.content;

        const messageEl = document.createElement('p');
        messageEl.textContent = message;
        messageEl.style.cssText = MODAL_STYLES.message;

        content.appendChild(messageEl);

        // Create footer
        const footer = document.createElement('div');
        footer.style.cssText = MODAL_STYLES.footer;

        // OK button styles based on type
        const confirmBtn = document.createElement('button');
        confirmBtn.textContent = confirmText;

        const styles = getButtonColorScheme(confirmType);

        confirmBtn.style.cssText = MODAL_STYLES.baseButton;
        confirmBtn.style.background = styles.background;
        confirmBtn.style.color = styles.color;
        confirmBtn.style.borderColor = styles.borderColor;
        confirmBtn.dataset.hoverBg = styles.hoverBg;
        confirmBtn.dataset.hoverBorder = styles.hoverBorder;
        confirmBtn.dataset.activeBg = styles.activeBg;
        confirmBtn.onmouseover = () => {
            confirmBtn.style.background = confirmBtn.dataset.hoverBg!;
            confirmBtn.style.borderColor = confirmBtn.dataset.hoverBorder!;
            confirmBtn.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
        };
        confirmBtn.onmouseout = () => {
            confirmBtn.style.background = styles.background;
            confirmBtn.style.borderColor = styles.borderColor;
            confirmBtn.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
        };
        confirmBtn.onmousedown = () => {
            confirmBtn.style.transform = 'scale(0.98)';
            confirmBtn.style.background = confirmBtn.dataset.activeBg!;
            confirmBtn.style.boxShadow = 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)';
        };
        confirmBtn.onmouseup = () => {
            confirmBtn.style.transform = 'scale(1)';
        };

        footer.appendChild(confirmBtn);

        // Assemble modal
        wrapper.appendChild(header);
        wrapper.appendChild(content);
        wrapper.appendChild(footer);

        container.appendChild(backdrop);
        container.appendChild(wrapper);

        // Add animations
        const styleId = 'confirm-modal-animations';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = MODAL_ANIMATIONS;
            document.head.appendChild(style);
        }

        // Cleanup function with animation
        const cleanup = () => {
            // Trigger exit animations
            container.style.animation = 'backdropFadeOut 0.2s ease forwards';
            wrapper.style.animation = 'modalScaleOut 0.33s ease forwards';

            // Wait for animation to complete before removing
            setTimeout(() => {
                if (document.body.contains(container)) {
                    document.body.removeChild(container);
                }
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            }, 330);
        };

        // Event handlers
        const handleDismiss = () => {
            cleanup();
            resolve();
        };

        confirmBtn.onclick = handleDismiss;
        closeBtn.onclick = handleDismiss;
        backdrop.onclick = handleDismiss;

        // Handle ESC key
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleDismiss();
                document.removeEventListener('keydown', handleEsc);
            }
        };
        document.addEventListener('keydown', handleEsc);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '15px';

        // Add to DOM
        document.body.appendChild(container);
    });
}
